import React, { useState, useEffect, useCallback } from "react";
import {
  //  Grid, // REMOVIDO: Eliminando o componente Grid
  CircularProgress,
  Box,
  Typography,
  Pagination,
  Alert,
  Button,
} from "@mui/material";
import { API_URLS } from "../../config/apiUrls";
import CardSorteio from "../CardSorteio";

export type SystemType = "ANUNCIO360" | "ESCALA11";

// Interface para o formato de um único sorteio retornado pela API (inalterada)
interface Draw {
  id: string;
  title: string;
  description: string;
  startDate: string;
  drawDate: string;
  displayEndDate: string;
  minNumber: number;
  maxNumber: number;
  ticketsPerUser: number;
  participationRule: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  tickets: any[];
  participantsCount: number;
  availableNumbers: number;
  totalPrize: number;
  prizePercentage: number;
  prizeInfo: {
    basePrize: number;
    currentPrize: number;
    totalPrizeIncrease: number;
    approvedAdsCount: number;
    totalApprovedAdsValue: number;
    hasDynamicPrize: false;
    hasFixedPrize: boolean;
    hasPercentageOnly: boolean;
  };
}

// Interface para a resposta completa da API de sorteios (inalterada)
interface SorteiosResponse {
  draws: Draw[];
  total: number;
}

// Interface para as propriedades recebidas pelo componente ListaSorteio (inalterada)
interface ListaSorteioProps {
  token: string;
  role: "MASTER" | "PARTNER" | "ANALYST";
  system: SystemType;
}

const ListaSorteio: React.FC<ListaSorteioProps> = ({ token, role, system }) => {
  const [sorteios, setSorteios] = useState<Draw[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(6);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [filterActive, setFilterActive] = useState<boolean>(true);

  const escala11ButtonStyleActive = {
    mr: 1,
    backgroundColor: "#008870",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#005848ff",
    },
  };

  const escala11ButtonStyleOutline = {
    color: "#008870",
    border: "1px solid #008870",
    "&:hover": {
      color: "#005848ff",
      border: "1px solid #005848ff",
    },
  };

  const isEscala11 = system === "ESCALA11";

  // Função para buscar os sorteios da API (inalterada)
  const fetchSorteios = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API_URLS.sorteios}?page=${page}&limit=${limit}`;

      if (filterActive) {
        url += `&active=true`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(
          `Erro ao buscar sorteios: ${response.status} ${response.statusText}`
        );
      }

      const data: SorteiosResponse = await response.json();
      setSorteios(data.draws);
      setTotalPages(Math.ceil(data.total / limit));
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Ocorreu um erro desconhecido ao carregar os sorteios.");
      }
    } finally {
      setLoading(false);
    }
  }, [page, limit, token, filterActive]);

  // useEffect para chamar fetchSorteios quando a página, limite, token ou filtro mudam (inalterado)
  useEffect(() => {
    if (token) {
      fetchSorteios();
    }
  }, [fetchSorteios, token]);

  // Lida com a mudança de página da paginação (inalterada)
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  // Lida com a mudança do filtro para "Ativos" (inalterada)
  const handleFilterActive = () => {
    if (!filterActive) {
      setFilterActive(true);
      setPage(1);
    }
  };

  // Lida com a mudança do filtro para "Todos" (inalterada)
  const handleFilterAll = () => {
    if (filterActive) {
      setFilterActive(false);
      setPage(1);
    }
  };

  // Callback para ser passado aos Cards/Modais, para recarregar a lista principal (inalterada)
  const handleSorteioUpdate = () => {
    setPage(1);
    fetchSorteios();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
          p: 3,
        }}
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Carregando sorteios...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Alert severity="error">
          <Typography variant="h6">Erro ao carregar sorteios:</Typography>
          <Typography>{error}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Lista de Sorteios
        </Typography>
        <Box>
          <Button
            variant={filterActive ? "contained" : "outlined"}
            onClick={handleFilterActive}
            sx={{
              ...(isEscala11
                ? filterActive
                  ? escala11ButtonStyleActive
                  : { ...escala11ButtonStyleOutline, mr: 1 }
                : { mr: 1 }),
            }}
          >
            Sorteios Ativos
          </Button>
          <Button
            variant={!filterActive ? "contained" : "outlined"}
            onClick={handleFilterAll}
            sx={
              isEscala11
                ? !filterActive
                  ? escala11ButtonStyleActive
                  : escala11ButtonStyleOutline
                : {}
            }
          >
            Todos Sorteios
          </Button>
        </Box>
      </Box>

      {sorteios.length === 0 ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            {filterActive
              ? "Nenhum sorteio ativo encontrado."
              : "Nenhum sorteio encontrado."}
          </Typography>
        </Box>
      ) : (
        // SUBSTITUINDO GRID CONTAINER E GRID ITEM POR BOX FLEXBOX
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            margin: "-12px", // Simula spacing={3} (24px, compensado com -12px)
          }}
        >
          {sorteios.map((sorteio) => (
            // Substituindo <Grid item key={sorteio.id} xs={12} sm={6} md={4}>
            <Box
              key={sorteio.id}
              sx={{
                padding: "12px", // Simula o spacing={3}
                // Larguras para simular xs={12}, sm={6}, md={4}
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "33.333%",
                },
              }}
            >
              <CardSorteio
                {...sorteio}
                role={role}
                token={token}
                onSorteioUpdated={handleSorteioUpdate}
                system={system}
              />
            </Box>
          ))}
        </Box>
      )}

      {totalPages > 1 && ( // Só mostra a paginação se houver mais de uma página
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
            sx={
              isEscala11
                ? {
                    "& .MuiPaginationItem-root.Mui-selected": {
                      backgroundColor: "#008870",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "#005848ff",
                      },
                    },
                  }
                : {}
            }
          />
        </Box>
      )}
    </Box>
  );
};

export default ListaSorteio;
